import type { ErrorTranslator, JsonSchema } from "@jsonforms/core";
import type { ErrorObject } from "ajv";

const REQUIRED = "required";
const MESSAGES = "x-validation-messages";
type MessageSchema = JsonSchema & { "x-validation-messages"?: Record<string, string> };

function tokens(pointer: string): string[] {
  return pointer ? pointer.slice(1).split("/").map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~")) : [];
}

function fieldSchema(root: JsonSchema, parts: string[]): MessageSchema {
  let current: JsonSchema = root;
  for (const part of parts) {
    current = current.items && !Array.isArray(current.items)
      ? current.items : current.properties?.[part] ?? {};
  }
  return current as MessageSchema;
}

function valueAt(data: unknown, parts: string[]): unknown {
  let value: unknown = data;
  for (const part of parts) {
    value = value && typeof value === "object" ? (value as Record<string, unknown>)[part] : undefined;
  }
  return value;
}

/** JSON Forms extension point; messages are compiler output, not another UI-authored catalogue. */
export function createCatalogErrorTranslator(schema: JsonSchema, data: unknown): ErrorTranslator {
  return (error) => resolveCatalogError(schema, data, error).message;
}

export function resolveCatalogError(schema: JsonSchema, data: unknown, error: ErrorObject) {
    const parts = tokens(error.instancePath);
    if (error.keyword === REQUIRED && typeof error.params.missingProperty === "string") {
      parts.push(error.params.missingProperty);
    }
    const field = fieldSchema(schema, parts);
    const parent = fieldSchema(schema, parts.slice(0, -1));
    const value = valueAt(data, parts);
    const empty = value == null || typeof value === "string" && value.trim() === "";
    const keyword = empty && parent.required?.includes(parts[parts.length - 1]) ? REQUIRED : error.keyword;
    const ruleParts = tokens(error.schemaPath.replace(/^#/, ""));
    let ruleMessage: string | undefined;
    let ruleId: string | undefined;
    let dependencies: string[] | undefined;
    let requirementField: string | undefined;
    let e2bRule: boolean | undefined;
    while (ruleParts.length > 0) {
      const node = valueAt(schema, ruleParts) as Record<string, unknown> | undefined;
      if (node && typeof node["x-rule-message"] === "string") {
        ruleMessage = node["x-rule-message"];
        ruleId = typeof node["x-rule-id"] === "string" ? node["x-rule-id"] : undefined;
        dependencies = Array.isArray(node["x-rule-fields"]) ? node["x-rule-fields"] as string[] : undefined;
        requirementField = typeof node["x-requirement-field"] === "string"
          ? node["x-requirement-field"] : undefined;
        e2bRule = node["x-rule-e2b"] === true ? true : undefined;
        break;
      }
      ruleParts.pop();
    }
    const message = ruleMessage ?? field[MESSAGES]?.[keyword] ?? field[MESSAGES]?.type ?? `${field.title ?? "Field"} has an invalid value.`;
    const instancePath = parts.length ? "/" + parts.map((part) => part.replace(/~/g, "~0").replace(/\//g, "~1")).join("/") : "";
    return {
      instancePath,
      keyword,
      message,
      ...(ruleId ? { ruleId, dependencies, requirementField, ...(e2bRule ? { e2bRule } : {}) } : {}),
    };
}
